import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vacancyService } from '../../services/vacancyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import api from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';

const CreateVacancy = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const { t, locale } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [jobTitles, setJobTitles] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    loadJobTitles();
    loadSkills();
  }, []);

  const loadJobTitles = async () => {
    try {
      const response = await api.get('/job-titles');
      if (Array.isArray(response.data)) {
        setJobTitles(response.data);
      } else if (response.data?.data) {
        setJobTitles(response.data.data);
      }
    } catch (error) {
      console.error('Error loading job titles:', error);
    }
  };

  const loadSkills = async () => {
    try {
      const response = await api.get('/skills');
      if (Array.isArray(response.data)) {
        setSkills(response.data);
      } else if (response.data?.data) {
        setSkills(response.data.data);
      }
    } catch (error) {
      console.error('Error loading skills:', error);
    }
  };

  const handleSkillToggle = (skillId) => {
    setSelectedSkills(prev => {
      if (prev.includes(skillId)) {
        return prev.filter(id => id !== skillId);
      } else {
        return [...prev, skillId];
      }
    });
  };

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);

      // Convert location to proper format
      if (data.location_lat && data.location_long) {
        data.location = {
          lat: parseFloat(data.location_lat),
          long: parseFloat(data.location_long),
        };
        delete data.location_lat;
        delete data.location_long;
      }

      // Add selected skills
      if (selectedSkills.length > 0) {
        data.skills = selectedSkills;
      }

      const response = await vacancyService.create(data);
      if (response.success) {
        toast.success(t('company.createVacancy.createSuccess'));
        navigate('/company/vacancies');
      }
    } catch (error) {
      const message = error.response?.data?.message || t('company.createVacancy.createFailed');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <CompanyNav user={user} />

      {/* Header Section */}
      <div className="px-3 mb-4 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="section-title mb-1">{t('company.createVacancy.title')}</h3>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>{t('company.createVacancy.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="apply-form px-3 mb-5">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.basicInfo')}</h3>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="job_title_id">
                {t('company.createVacancy.jobTitle')} <span style={{ color: '#608fd3' }}>*</span>
              </label>
              <select
                id="job_title_id"
                className="edit-profile-input"
                {...register('job_title_id', {
                  required: t('company.createVacancy.jobTitleRequired'),
                })}
              >
                <option value="">{t('company.createVacancy.selectJobTitle')}</option>
                {jobTitles.map((jobTitle) => (
                  <option key={jobTitle.id} value={jobTitle.id}>
                    {jobTitle.name}
                  </option>
                ))}
              </select>
              {errors.job_title_id && (
                <span className="edit-profile-error">{errors.job_title_id.message}</span>
              )}
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="type_of_work">{t('company.createVacancy.workType')}</label>
              <select id="type_of_work" className="edit-profile-input" {...register('type_of_work')}>
                <option value="">{t('company.createVacancy.selectType')}</option>
                <option value="full_time">{t('employee.home.workType.fullTime')}</option>
                <option value="part_time">{t('employee.home.workType.partTime')}</option>
              </select>
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="presence_type">{t('company.createVacancy.presenceType')}</label>
              <select id="presence_type" className="edit-profile-input" {...register('presence_type')}>
                <option value="">{t('company.createVacancy.selectType')}</option>
                <option value="remote">{t('employee.home.presenceType.remote')}</option>
                <option value="hybrid">{t('employee.home.presenceType.hybrid')}</option>
                <option value="onsite">{t('employee.home.presenceType.onsite')}</option>
              </select>
            </div>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="experience">{t('company.createVacancy.experience')}</label>
                <input
                  type="number"
                  id="experience"
                  min="0"
                  className="edit-profile-input"
                  {...register('experience')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="academic_degree">{t('company.createVacancy.academicDegree')}</label>
                <input
                  type="text"
                  id="academic_degree"
                  className="edit-profile-input"
                  placeholder={t('company.createVacancy.academicDegreePlaceholder')}
                  {...register('academic_degree')}
                />
              </div>
            </div>
          </div>

          {/* Salary & Location */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.salaryLocation')}</h3>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="salary">{t('company.createVacancy.salary')}</label>
                <input
                  type="number"
                  id="salary"
                  step="0.01"
                  min="0"
                  className="edit-profile-input"
                  {...register('salary')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="currency">{t('company.createVacancy.currency')}</label>
                <select id="currency" className="edit-profile-input" {...register('currency')}>
                  <option value="USD">{t('company.createVacancy.USD')}</option>
                  <option value="ُEUR">{t('company.createVacancy.EUR')}</option>
                  <option value="ُEGP">{t('company.createVacancy.EGP')}</option>

                </select>
              </div>
            </div>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="location_lat">{t('company.createVacancy.latitude')}</label>
                <input
                  type="number"
                  id="location_lat"
                  step="0.000001"
                  className="edit-profile-input"
                  {...register('location_lat')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="location_long">{t('company.createVacancy.longitude')}</label>
                <input
                  type="number"
                  id="location_long"
                  step="0.000001"
                  className="edit-profile-input"
                  {...register('location_long')}
                />
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.details')}</h3>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="description">{t('company.createVacancy.description')}</label>
              <textarea
                id="description"
                rows="6"
                className="notes-textarea"
                placeholder={t('company.createVacancy.descriptionPlaceholder')}
                {...register('description')}
              />
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="privileges">{t('company.createVacancy.privileges')}</label>
              <textarea
                id="privileges"
                rows="4"
                className="notes-textarea"
                placeholder={t('company.createVacancy.privilegesPlaceholder')}
                {...register('privileges')}
              />
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="status">{t('company.createVacancy.status')}</label>
              <select id="status" className="edit-profile-input" {...register('status')}>
                <option value="open">{t('company.createVacancy.open')}</option>
                <option value="closed">{t('company.createVacancy.closed')}</option>
              </select>
            </div>
          </div>

          {/* Skills Section */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.skills')}</h3>
            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>{t('company.createVacancy.skillsDescription')}</p>
            <div className="d-flex flex-wrap gap-2">
              {skills.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => handleSkillToggle(skill.id)}
                  className={`btn ${selectedSkills.includes(skill.id) ? 'btn-apply' : 'btn-view-vacancy'}`}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    border: selectedSkills.includes(skill.id) ? 'none' : '1px solid #ddd',
                    background: selectedSkills.includes(skill.id)
                      ? 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)'
                      : 'white',
                    color: selectedSkills.includes(skill.id) ? 'white' : '#333',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {skill.name}
                </button>
              ))}
            </div>
            {skills.length === 0 && (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>{t('company.createVacancy.noSkillsAvailable')}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className="d-flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/company/vacancies')}
              className="btn-view-vacancy"
              style={{ flex: 1 }}
            >
              {t('company.createVacancy.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-apply"
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                color: 'white',
                padding: '0.875rem 1.5rem',
                borderRadius: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none'
              }}
            >
              {submitting ? t('company.createVacancy.creating') : t('company.createVacancy.create')}
            </button>
          </div>
        </form>
      </div>

      <CompanyBottomNavigation />
    </div>
  );
};

export default CreateVacancy;



